import { useTranslation } from 'react-i18next';
import { Wrench, Lightbulb, Snowflake, Building2, Sun, CheckCircle, ArrowRight, X, ChevronRight, Zap, Cog } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { expertises } from '../../constants/expertisesData';
import { def1, def2, def3, def4, def5, def6, def7, def8, def9 } from '../../assets/images';

const expertiseConfig = {
  1: { icon: Zap, accent: "#fbbf24", bg: "from-amber-100 to-amber-50", color: "text-amber-600", border: "border-amber-200" },
  2: { icon: Building2, accent: "#818cf8", bg: "from-indigo-100 to-indigo-50", color: "text-indigo-600", border: "border-indigo-200" },
  3: { icon: Snowflake, accent: "#22d3ee", bg: "from-cyan-100 to-cyan-50", color: "text-cyan-600", border: "border-cyan-200" },
  4: { icon: Cog, accent: "#94a3b8", bg: "from-slate-100 to-slate-50", color: "text-slate-600", border: "border-slate-200" },
  5: { icon: Wrench, accent: "#fb923c", bg: "from-orange-100 to-orange-50", color: "text-orange-600", border: "border-orange-200" },
  6: { icon: Sun, accent: "#34d399", bg: "from-emerald-100 to-emerald-50", color: "text-emerald-600", border: "border-emerald-200" },
};

const Expertises = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language && i18n.language.startsWith('en') ? 'en' : 'fr';
  const bgSlides = [def1, def2, def3, def4, def5, def6, def7, def8, def9];
  const [bgSlide, setBgSlide] = useState(3); // Start at def4
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setBgSlide((prev) => (prev + 1) % bgSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [bgSlides.length]);

  // Block body scroll when modal is open
  useEffect(() => {
    if (selectedId) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = 'var(--scrollbar-width, 0px)';
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [selectedId]);

  return (
    <section id="expertises" className={`section-padding bg-white dark:bg-auxis-charcoal relative py-24 font-quicksand transition-colors duration-300 ${selectedId ? 'z-[999] overflow-visible' : 'overflow-hidden z-10'}`}>
      {/* Background Image Carousel */}
      <div className="absolute inset-0">
        <AnimatePresence mode="wait">
          <motion.img 
            key={bgSlide}
            src={bgSlides[bgSlide]} 
            alt={`AUXIS Background ${bgSlide + 1}`}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-auxis-charcoal/90 via-auxis-charcoal/70 to-auxis-charcoal/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-auxis-charcoal via-transparent to-auxis-charcoal/30" />
      </div>

      <div className="container-wide relative z-10 px-6 max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-auxis-gold/10 border border-auxis-gold/20 text-xs font-bold uppercase tracking-widest text-auxis-gold mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-auxis-gold animate-pulse" />
              {t('expertises.tag')}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight leading-tight">
              {t('expertises.title1')} <br/> <span className="text-transparent bg-clip-text bg-gradient-to-r from-auxis-gold to-yellow-300">{t('expertises.title2')}</span>
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed font-light max-w-2xl mx-auto">
              {t('expertises.desc')}
            </p>
          </motion.div>
        </div>

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
          {expertises.map((exp, index) => {
            const config = expertiseConfig[exp.id] || expertiseConfig[5];
            const Icon = config.icon || Wrench;
            
            return (
              <motion.div
                key={exp.id}
                layoutId={`card-container-${exp.id}`}
                onClick={() => setSelectedId(exp.id)}
                className="group relative rounded-[2rem] cursor-pointer overflow-hidden bg-white/10 backdrop-blur-md border border-white/20 hover:border-auxis-gold/50 transition-all duration-500 shadow-lg hover:shadow-xl hover:shadow-auxis-gold/20 flex flex-col h-full min-h-[320px]"
                whileHover={{ y: -5 }}                
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                {/* Gradient Blob Background */}
                <div 
                    className={`absolute inset-0 bg-gradient-to-br ${config.bg} opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none dark:opacity-10 dark:group-hover:opacity-20`} 
                />
                
                <div className="p-8 flex flex-col h-full relative z-10">
                   {/* Top: Icon & Arrow */}
                   <div className="flex justify-between items-start mb-auto">
                        <div className={`w-14 h-14 rounded-2xl bg-white/20 border border-white/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-500 shadow-sm ${config.color}`}>
                            <Icon size={28} strokeWidth={1.5} />
                        </div>
                        <div className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 translate-x-4 group-hover:translate-x-0 transition-all duration-500 bg-auxis-gold text-auxis-charcoal shadow-lg">
                            <ArrowRight size={18} />
                        </div>
                   </div>

                   {/* Middle: Spacer */}
                   <div className="h-6" />

                   {/* Bottom: Text */}
                   <div>
                        <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-auxis-gold transition-colors duration-300">
                            {currentLang === 'fr' ? exp.title_fr : exp.title_en}
                        </h3>
                        <p className="text-sm text-gray-300 line-clamp-2 leading-relaxed mb-6 group-hover:text-gray-200 transition-colors">
                            {currentLang === 'fr' ? exp.description_fr : exp.description_en}
                        </p>
                        
                        {/* Interactive Footer in Card */}
                        <div className="flex items-center gap-3 pt-4 border-t border-white/20 group-hover:border-auxis-gold/30 transition-colors mt-auto">
                           <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 group-hover:text-gray-300 transition-colors">
                             {t('expertises.learnMore')}
                           </span>
                           <div className="h-px flex-1 bg-white/20 group-hover:bg-auxis-gold/30 transition-colors" />
                           <span className="text-[10px] font-mono text-gray-500">
                             0{index + 1}
                           </span>
                        </div>
                   </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Expanded Modal Overlay */}
        <AnimatePresence>
          {selectedId && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 md:p-6 lg:p-8 h-screen w-screen">
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setSelectedId(null)}
                className="absolute inset-0 bg-auxis-charcoal/80 dark:bg-black/80 backdrop-blur-md"
              />

              {/* Modal Content */}
              <motion.div
                layoutId={`card-container-${selectedId}`}
                className="w-full max-w-6xl bg-white dark:bg-auxis-charcoal border border-white/50 dark:border-white/10 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl relative max-h-[90vh] sm:max-h-[88vh] md:max-h-[85vh] flex flex-col lg:flex-row z-10 ring-1 ring-black/5"
                onClick={(e) => e.stopPropagation()}
              >
                  {(() => {
                      const exp = expertises.find(e => e.id === selectedId);
                      const config = expertiseConfig[exp.id] || expertiseConfig[5];
                      const Icon = config.icon || Wrench;

                      return (
                          <>
                              {/* Close Button Mobile */}
                              <button 
                                  onClick={() => setSelectedId(null)}
                                  className="absolute top-3 right-3 sm:top-4 sm:right-4 z-50 p-2.5 rounded-full bg-white/90 dark:bg-auxis-charcoal/90 text-auxis-charcoal dark:text-white lg:hidden backdrop-blur-md shadow-lg hover:bg-auxis-gold hover:text-white transition-colors duration-300"
                              >
                                  <X size={20} strokeWidth={2.5} />
                              </button>

                              {/* Sidebar / Visual Side */}
                              <div className="lg:w-1/3 bg-gray-50/50 dark:bg-auxis-charcoal-light/50 p-6 sm:p-8 lg:p-10 xl:p-12 relative overflow-hidden flex flex-col justify-between border-b lg:border-b-0 lg:border-r border-gray-100 dark:border-white/10 min-h-[200px] sm:min-h-[240px] lg:min-h-0 shrink-0">
                                  <div className={`absolute inset-0 bg-gradient-to-br ${config.bg} opacity-50 dark:opacity-10`} />
                                  <div className="absolute top-0 right-0 p-32 bg-white/40 dark:bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
                                  
                                  <div className="relative z-10">
                                      <div className={`w-16 h-16 sm:w-18 sm:h-18 lg:w-20 lg:h-20 rounded-2xl sm:rounded-3xl bg-white dark:bg-auxis-charcoal border border-white/50 dark:border-white/10 flex items-center justify-center mb-6 sm:mb-8 shadow-xl ${config.color}`}>
                                          <Icon size={36} strokeWidth={1.5} className="sm:w-10 sm:h-10" />
                                      </div>
                                      <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-auxis-charcoal dark:text-white mb-4 sm:mb-6 leading-tight">
                                          {currentLang === 'fr' ? exp.title_fr : exp.title_en}
                                      </h3>
                                      <p className="text-auxis-charcoal/60 dark:text-gray-400 leading-relaxed text-sm sm:text-base border-l-2 border-auxis-charcoal/10 dark:border-white/10 pl-3 sm:pl-4 py-1">
                                          {currentLang === 'fr' ? exp.description_fr : exp.description_en}
                                      </p>
                                  </div>

                                  <div className="relative z-10 mt-auto pt-10 hidden lg:block">
                                      <div className="flex items-center gap-4 text-auxis-charcoal/30 dark:text-gray-600 text-sm font-mono">
                                          <span>EXP.0{selectedId}</span>
                                          <div className="h-px w-10 bg-auxis-charcoal/10 dark:bg-white/10" />
                                          <span>AUXIS</span>
                                      </div>
                                  </div>
                              </div>

                              {/* Content Side */}
                              <div className="lg:w-2/3 p-5 sm:p-6 md:p-8 lg:p-10 xl:p-12 overflow-y-auto bg-white dark:bg-auxis-charcoal scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700 hover:scrollbar-thumb-gray-300 dark:hover:scrollbar-thumb-gray-600">
                                  <div className="sticky top-0 bg-white/95 dark:bg-auxis-charcoal/95 backdrop-blur-xl z-20 pb-6 mb-6 border-b border-gray-100 dark:border-white/10 flex justify-between items-center -mx-2 px-2">
                                      <h4 className="text-xs font-bold text-auxis-charcoal dark:text-white uppercase tracking-[0.2em] flex items-center gap-3">
                                          <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: config.accent }} />
                                          {t('expertises.modalTitle')}
                                      </h4>
                                      <button 
                                          onClick={() => setSelectedId(null)}
                                          className="hidden lg:flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-auxis-charcoal/40 dark:text-gray-500 hover:text-auxis-charcoal dark:hover:text-white transition-colors group px-3 py-1 rounded-full border border-gray-100 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-auxis-charcoal-light"
                                      >
                                          {t('expertises.close')} <X size={14} />
                                      </button>
                                  </div>

                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 lg:gap-x-8 gap-y-8 sm:gap-y-10 pb-16 sm:pb-20">
                                      {exp.subcategories?.map((sub, idx) => (
                                          <motion.div 
                                            key={idx} 
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.1 + idx * 0.05 }}
                                            className="space-y-4 group/item"
                                          >
                                              <h5 className="text-auxis-charcoal dark:text-white font-bold text-base md:text-lg flex items-center gap-3 border-b border-gray-100 dark:border-white/10 pb-2">
                                                  <span className={`text-sm opacity-50 font-mono`} style={{ color: config.accent }}>0{idx + 1}.</span>
                                                  {currentLang === 'fr' ? sub.title_fr : sub.title_en}
                                              </h5>
                                              <ul className="space-y-2.5">
                                                  {(currentLang === 'fr' ? sub.items_fr : sub.items_en)?.map((item, itemIdx) => (
                                                      <li key={itemIdx} className="flex items-start gap-3 text-sm text-auxis-charcoal/60 dark:text-gray-400 group-hover/item:text-auxis-charcoal/80 dark:group-hover/item:text-gray-300 transition-colors pl-2 relative">
                                                          <span className="mt-1.5 w-1 h-1 rounded-full bg-auxis-charcoal/20 dark:bg-white/20 shrink-0" />
                                                          <span className="leading-relaxed">{item}</span>
                                                      </li>
                                                  ))}
                                              </ul>
                                          </motion.div>
                                      ))}
                                  </div>
                                  
                                  {/* Bottom CTA Gradient */}
                                  <div className="absolute bottom-0 left-0 right-0 h-24 sm:h-32 bg-gradient-to-t from-white dark:from-auxis-charcoal to-transparent pointer-events-none" />
                                  <div className="sticky bottom-3 sm:bottom-4 mx-auto max-w-md text-center">
                                     <button 
                                        onClick={() => {
                                            setSelectedId(null);
                                            document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                        className="shadow-xl shadow-auxis-charcoal/10 dark:shadow-black/50 bg-auxis-charcoal dark:bg-auxis-gold text-white dark:text-auxis-charcoal px-5 sm:px-6 py-2.5 sm:py-3 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-widest hover:bg-auxis-gold dark:hover:bg-white hover:text-white dark:hover:text-auxis-charcoal transition-all duration-300 translate-y-0 hover:-translate-y-1 w-full sm:w-auto"
                                     >
                                       {t('expertises.quote')}
                                     </button>
                                  </div>
                              </div>
                          </>
                      );
                  })()}
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Expertises;
